"use client";

import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { HfTourOffer, HfWindowKey } from "../model";
import { CheckIcon, CloseIcon } from "../icons";
import { WINDOW_LABELS, picksOf, preferredTimesOf, pruneChoice, visibleWindows, zoneCity, zoneDifferenceLabel, type TourChoice } from "../tours";

/**
 * Contact and tour requests (spec 5.2 `POST /inquiries`, through the storefront). Tour times are chips, never a date
 * input: the days and windows the organization offers (`GET /tour-windows`), sent as up to five day-and-window `picks` with the
 * visitor's time zone. An API without that endpoint gets today's six days and `preferredTimes`. The operator confirms one of them,
 * and the copy says so. The answer is the same for every request (202), so success never says more than "sent".
 */

type TurnstileApi = {
  render: (element: HTMLElement, options: { sitekey: string; callback: (token: string) => void; "expired-callback": () => void; "error-callback": () => void }) => string;
  remove: (widgetId: string) => void;
};
const turnstileApi = () => (window as unknown as { turnstile?: TurnstileApi }).turnstile;

const TURNSTILE = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export type { TourChoice } from "../tours";

/** The visitor's IANA zone, or null where the browser will not say. */
export function browserZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** What a tour request sends: `org` offers day-and-window picks and the visitor's zone, `legacy` the old instants only; both keep `preferredTimes`. */
export function tourPayload(offer: HfTourOffer, choice: TourChoice, zone: string | null) {
  const picks = picksOf(offer, choice);
  return {
    picks: offer.source === "org" ? picks : undefined,
    timeZone: offer.source === "org" ? zone ?? undefined : undefined,
    preferredTimes: preferredTimesOf(picks, offer.maxPicks),
    count: picks.length,
  };
}

function TurnstileWidget({ siteKey, onToken }: { siteKey: string; onToken: (token: string | null) => void }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let widget: string | undefined;
    let cancelled = false;
    const ready = turnstileApi()
      ? Promise.resolve()
      : new Promise<void>((resolve, reject) => {
          const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE}"]`);
          const script = existing ?? Object.assign(document.createElement("script"), { src: TURNSTILE, async: true });
          script.addEventListener("load", () => resolve(), { once: true });
          script.addEventListener("error", () => reject(new Error("turnstile")), { once: true });
          if (!existing) document.head.appendChild(script);
        });
    ready.then(
      () => {
        const api = turnstileApi();
        if (cancelled || !box.current || !api) return;
        widget = api.render(box.current, { sitekey: siteKey, callback: onToken, "expired-callback": () => onToken(null), "error-callback": () => onToken(null) });
      },
      () => onToken(null),
    );
    return () => {
      cancelled = true;
      if (widget) turnstileApi()?.remove(widget);
    };
  }, [siteKey, onToken]);
  return <div className="hf-turnstile" ref={box} />;
}

/**
 * Day and window chips. Only the offered days show, and only windows open on at least one picked day. Pairs are capped (five); a chip
 * that would pass that says so instead of silently dropping one. When the organization's zone is not the visitor's, the chips say
 * whose clock the windows are on.
 */
export function TourChips({ offer, choice, onChange, legend = "Days that suit you" }: { offer: HfTourOffer; choice: TourChoice; onChange: (next: TourChoice) => void; legend?: string }) {
  const [note, setNote] = useState<string | null>(null);
  const zone = offer.zoneId;
  const [clock, setClock] = useState<string | null>(null);
  useEffect(() => {
    const visitor = browserZone();
    const frame = requestAnimationFrame(() => {
      if (!zone || !visitor) return setClock(null);
      const difference = zoneDifferenceLabel({ orgZone: zone, visitorZone: visitor });
      setClock(difference ? `Times are ${zoneCity(zone)} time, ${difference} for you.` : null);
    });
    return () => cancelAnimationFrame(frame);
  }, [zone]);

  const shown = visibleWindows(offer, choice.days);
  const toggle = (next: TourChoice) => {
    const pruned = pruneChoice(offer, next);
    if (picksOf(offer, pruned).length > offer.maxPicks) {
      setNote(`Pick up to ${offer.maxPicks} day and time pairs.`);
      return;
    }
    setNote(null);
    onChange(pruned);
  };
  return (
    <div className="hf-chips-wrap">
      <div className="hf-chips hf-days" role="group" aria-label={legend} style={{ gridTemplateColumns: `repeat(${Math.min(Math.max(offer.days.length, 1), 6)}, minmax(0, 1fr))` }}>
        {offer.days.map((day) => {
          const on = choice.days.includes(day.date);
          return (
            <button key={day.date} type="button" aria-pressed={on} onClick={() => toggle({ ...choice, days: on ? choice.days.filter((d) => d !== day.date) : [...choice.days, day.date] })}>
              <span>{day.weekday}</span>
              <b>{day.day}</b>
            </button>
          );
        })}
      </div>
      <div className="hf-chips hf-times" role="group" aria-label="Times that suit you" style={{ gridTemplateColumns: `repeat(${Math.max(shown.length, 1)}, minmax(0, 1fr))` }}>
        {shown.map((key: HfWindowKey) => {
          const on = choice.slots.includes(key);
          const hours = offer.windows.find((window) => window.key === key)?.hours ?? null;
          return (
            <button key={key} type="button" aria-pressed={on} data-hours={hours ? "" : undefined} onClick={() => toggle({ ...choice, slots: on ? choice.slots.filter((s) => s !== key) : [...choice.slots, key] })}>
              <b>{WINDOW_LABELS[key]}</b>
              {hours ? <span>{hours}</span> : null}
            </button>
          );
        })}
      </div>
      <p className="hf-chips-note" role="status">
        {note ?? clock ?? ""}
      </p>
    </div>
  );
}

type Status = { kind: "idle" } | { kind: "sending" } | { kind: "sent" } | { kind: "error"; message: string };

export interface InquiryFormProps {
  action: string;
  /** Contact only, tour only, or both with a switch. */
  mode: "contact" | "tour" | "either";
  orgName: string;
  offer: HfTourOffer;
  listingPublicId?: string;
  turnstileSiteKey: string | null;
  /** A tour choice made outside the form (the action card's chips). */
  choice?: TourChoice;
  onChoice?: (next: TourChoice) => void;
  onDone?: () => void;
  autoFocus?: boolean;
}

export function InquiryForm(props: InquiryFormProps) {
  const id = useId();
  const [kind, setKind] = useState<"contact" | "tour">(props.mode === "tour" ? "tour" : "contact");
  const [ownChoice, setOwnChoice] = useState<TourChoice>({ days: [], slots: [] });
  const choice = props.choice ?? ownChoice;
  const setChoice = props.onChoice ?? setOwnChoice;
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const onToken = useCallback((value: string | null) => setToken(value), []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") ?? "").trim();
    const email = String(form.get("email") ?? "").trim();
    const phone = String(form.get("phone") ?? "").trim();
    const message = String(form.get("message") ?? "").trim();
    if (!name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus({ kind: "error", message: "Add your name and a valid email address." });
      return;
    }
    const tour = kind === "tour" ? tourPayload(props.offer, choice, browserZone()) : null;
    if (tour && tour.count === 0) {
      setStatus({ kind: "error", message: "Pick at least one day and one time." });
      return;
    }
    if (kind === "contact" && !message) {
      setStatus({ kind: "error", message: "Write a short message." });
      return;
    }
    if (props.turnstileSiteKey && !token) {
      setStatus({ kind: "error", message: "Please confirm you are not a robot, then send again." });
      return;
    }
    setStatus({ kind: "sending" });
    try {
      const response = await fetch(props.action, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          kind,
          name,
          email,
          phone: phone || undefined,
          message: message || undefined,
          listingPublicId: props.listingPublicId,
          picks: tour?.picks,
          timeZone: tour?.timeZone,
          preferredTimes: tour ? tour.preferredTimes : undefined,
          turnstileToken: token ?? "",
        }),
      });
      if (response.status === 202 || response.ok) setStatus({ kind: "sent" });
      else if (response.status === 429) setStatus({ kind: "error", message: "Too many requests from here. Please try again in a while." });
      else setStatus({ kind: "error", message: "That did not go through. Check the details and try again." });
    } catch {
      setStatus({ kind: "error", message: "That did not go through. Check your connection and try again." });
    }
  }

  if (status.kind === "sent") {
    return (
      <div className="hf-sent" role="status">
        <span className="hf-sent-ic" aria-hidden="true">
          <CheckIcon />
        </span>
        <h3>{kind === "tour" ? "Tour request sent" : "Message sent"}</h3>
        <p>{kind === "tour" ? `${props.orgName} will confirm one of your times by email.` : `${props.orgName} will reply by email.`}</p>
        {props.onDone ? (
          <button type="button" className="hf-btn hf-btn-tone" onClick={props.onDone}>
            Done
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <form className="hf-form" onSubmit={submit} noValidate>
      {props.mode === "either" ? (
        <div className="hf-switch" role="radiogroup" aria-label="What would you like?">
          {(["contact", "tour"] as const).map((value) => (
            <button key={value} type="button" role="radio" aria-checked={kind === value} onClick={() => setKind(value)}>
              {value === "contact" ? "Send a message" : "Ask for a tour"}
            </button>
          ))}
        </div>
      ) : null}
      {kind === "tour" ? (
        <fieldset className="hf-fieldset">
          <legend>Days and times that suit you</legend>
          <TourChips offer={props.offer} choice={choice} onChange={setChoice} />
          <p className="hf-help">{props.orgName} confirms one of these times with you.</p>
        </fieldset>
      ) : null}
      <div className="hf-field-row">
        <Field id={`${id}-name`} label="Name">
          <input id={`${id}-name`} name="name" autoComplete="name" required autoFocus={props.autoFocus} />
        </Field>
        <Field id={`${id}-email`} label="Email">
          <input id={`${id}-email`} name="email" type="email" autoComplete="email" required />
        </Field>
      </div>
      <Field id={`${id}-phone`} label="Phone" optional>
        <input id={`${id}-phone`} name="phone" type="tel" autoComplete="tel" />
      </Field>
      <Field id={`${id}-message`} label="Message" optional={kind === "tour"}>
        <textarea id={`${id}-message`} name="message" rows={kind === "tour" ? 2 : 4} />
      </Field>
      {props.turnstileSiteKey ? <TurnstileWidget siteKey={props.turnstileSiteKey} onToken={onToken} /> : null}
      {status.kind === "error" ? (
        <p className="hf-form-error" role="alert">
          {status.message}
        </p>
      ) : null}
      <button type="submit" className="hf-btn hf-btn-accent hf-form-send" disabled={status.kind === "sending"}>
        {status.kind === "sending" ? "Sending…" : kind === "tour" ? "Send tour request" : "Send message"}
      </button>
    </form>
  );
}

function Field({ id, label, optional = false, children }: { id: string; label: string; optional?: boolean; children: ReactNode }) {
  return (
    <div className="hf-field">
      <label htmlFor={id}>
        {label}
        {optional ? <span> (optional)</span> : null}
      </label>
      {children}
    </div>
  );
}

const OPEN_EVENT = "hf:tour";

/** The phone bar's "Tour" button: opens the tour request of the action card. */
export function TourButton({ label = "Tour" }: { label?: string }) {
  return (
    <button type="button" className="hf-btn hf-btn-tone" onClick={() => window.dispatchEvent(new Event(OPEN_EVENT))}>
      {label}
    </button>
  );
}

/**
 * The action card's tour request: chips in the card, then a dialog for the contact details. The phone bar opens the same
 * dialog. Without an inquiry endpoint the button falls back to email or phone.
 */
export function TourRequest(props: {
  action: string;
  orgName: string;
  title: string;
  listingPublicId: string;
  offer: HfTourOffer;
  turnstileSiteKey: string | null;
  applyHref: string | null;
  /** The card's price block, above the chips, and its question line below them (server-rendered). */
  top: ReactNode;
  bottom: ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const [choice, setChoice] = useState<TourChoice>({ days: [], slots: [] });
  const [round, setRound] = useState(0);
  // The sheet lives at the theme root, not in the action card: on a phone the card is hidden and opens from the bottom bar.
  const [host, setHost] = useState<Element | null>(null);
  useEffect(() => {
    const root = anchor.current?.closest(".hf") ?? null;
    const frame = requestAnimationFrame(() => setHost(root));
    return () => cancelAnimationFrame(frame);
  }, []);
  const open = useCallback(() => dialog.current?.showModal(), []);
  useEffect(() => {
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, [open]);
  const close = () => {
    dialog.current?.close();
    setRound((value) => value + 1);
  };

  return (
    <>
      <div className="hf-act-in">
        {props.top}
        <div className="hf-act-tour">
          <h2 className="hf-act-h">Ask for a tour</h2>
          <p className="hf-act-help">Pick the days and times that suit you. {props.orgName} confirms one.</p>
          <TourChips offer={props.offer} choice={choice} onChange={setChoice} />
        </div>
        {props.bottom}
      </div>
      <div className={props.applyHref ? "hf-act-btns" : "hf-act-btns hf-act-btns-one"}>
        <button type="button" className="hf-btn hf-btn-tone" onClick={open}>
          Request tour
        </button>
        {props.applyHref ? (
          <a className="hf-btn hf-btn-accent" href={props.applyHref}>
            Apply now
          </a>
        ) : null}
      </div>
      <span ref={anchor} hidden />
      {host
        ? createPortal(
          <dialog ref={dialog} className="hf-sheet" aria-labelledby="hf-tour-title" onClose={() => setRound((value) => value + 1)}>
            <div className="hf-sheet-head">
              <h2 id="hf-tour-title">Ask for a tour</h2>
              <button type="button" className="hf-btn hf-btn-tone hf-btn-icon" onClick={close} aria-label="Close">
                <CloseIcon />
              </button>
            </div>
            <p className="hf-sheet-sub">{props.title}</p>
            <InquiryForm
              key={round}
              action={props.action}
              mode="tour"
              orgName={props.orgName}
              offer={props.offer}
              listingPublicId={props.listingPublicId}
              turnstileSiteKey={props.turnstileSiteKey}
              choice={choice}
              onChoice={setChoice}
              onDone={close}
              autoFocus
            />
          </dialog>,
          host,
        )
        : null}
    </>
  );
}
